import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { useWindowWidth } from '@react-hook/window-size'
import { useNavigate } from 'react-router-dom'
import {
  DocumentChartBarIcon,
  PencilSquareIcon,
  WalletIcon,
  LockClosedIcon,
  KeyIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline'

import { UserFilters, UserListTable, ActionPanels } from '../components'
import ConfirmationDialog from '../../../components/ConfirmationDialog'
import CustomDialog from '../../../components/CustomDialog'
import {
  demoUserList,
  demoTransactionLogs,
  userRoles,
  userStatuses,
  balanceTransactionTypes,
} from '../../../utils/userControlDemoData';
import userService from '../../../services/userService';
import { LoadingSpinner } from '../../../components'
import PaginationV2 from '../../../components/pagination_v2'

const actionablePanels = ['logs', 'balance', 'password']

const PAGE_SIZE = 6

const actionConfig = [
  {
    id: 'logs',
    label: 'Transaction log',
    icon: DocumentChartBarIcon,
    accent: 'text-customAccent',
  },
  {
    id: 'edit',
    label: 'Edit profile',
    icon: PencilSquareIcon,
    accent: 'text-customMuted',
  },
  {
    id: 'balance',
    label: 'Balance update',
    icon: CurrencyDollarIcon,
    accent: 'text-customFontGreen',
  },
  {
    id: 'lock',
    label: 'Lock account',
    icon: LockClosedIcon,
    accent: 'text-customFontRed',
  },
  {
    id: 'password',
    label: 'Reset password',
    icon: KeyIcon,
    accent: 'text-customInk dark:text-customAccent',
  },
]

const UserControlPage = () => {
  const navigate = useNavigate()
  const onlyWidth = useWindowWidth()
  const isMobileView = onlyWidth < 768

  const [users, setUsers] = useState([])
  const [transactionLogs, setTransactionLogs] = useState(demoTransactionLogs)
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState(userRoles[0])
  const [statusFilter, setStatusFilter] = useState(userStatuses[0])
  const [selectedUserId, setSelectedUserId] = useState(users[0]?.id ?? null)
  const [activeAction, setActiveAction] = useState('profile')
  const [lockTarget, setLockTarget] = useState(null)
  const [statusMessage, setStatusMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [balanceForm, setBalanceForm] = useState({
    amount: '',
    type: balanceTransactionTypes[0]?.value ?? 'credit',
  })
  const [passwordForm, setPasswordForm] = useState({ password: '', confirm: '' })
  const [currentPage, setCurrentPage] = useState(1)
  const [isActionDialogOpen, setIsActionDialogOpen] = useState(false)

  const filteredUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    return users.filter(user => {
      const matchesSearch =
        !term ||
        user.username.toLowerCase().includes(term) ||
        user.id.toLowerCase().includes(term)
      const matchesRole =
        roleFilter.value === 'all' ||
        user.role.toLowerCase() === roleFilter.value.toLowerCase()
      const matchesStatus =
        statusFilter.value === 'all' ||
        user.status.toLowerCase() === statusFilter.value.toLowerCase()

      return matchesSearch && matchesRole && matchesStatus
    })
  }, [users, searchTerm, roleFilter, statusFilter])

  const totalUsers = filteredUsers.length
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalUsers / PAGE_SIZE)),
    [totalUsers],
  )

  useEffect(() => {
    setCurrentPage(1)
  }, [totalUsers])

  useEffect(() => {
    if (!isMobileView) {
      setIsActionDialogOpen(false)
    }
  }, [isMobileView])

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE
    return filteredUsers.slice(startIndex, startIndex + PAGE_SIZE)
  }, [filteredUsers, currentPage])

  const paginationStart = totalUsers === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1
  const paginationEnd = totalUsers === 0 ? 0 : Math.min(currentPage * PAGE_SIZE, totalUsers)

  const fetchUserLists = async () => {
    setIsLoading(true);
    try {
      const response = await userService.getUserList();

      if (response?.status === 200 && Array.isArray(response.data.userLists)) {
        setUsers(
          response.data.userLists.filter(user => user.roleId !== 1).sort((a,b) => a.roleId- b.roleId).map(user => ({
            id: user.userName,
            username: user.name,
            role: user.roleName,
            balance: user.balance,
            status: user.lock ? "Locked" : "Active",
            roleId: user.roleId,
            userId: user.userId,
            sharePercent: user.sharePercent,
            betLimitForSingle: user.betLimitForSingle,
            betLimitForMix: user.betLimitForMix,
            isShare: user.isShare,
            lock: user.lock,
            lastLoginDate: user.lastLoginDate
          }))
        );
      } else {
        console.warn("Unexpected response:", response);
        setUsers([]);
      }
    } catch (e) {
      console.error("Failed to fetch result hub data", e);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserLists()
  }, [])

  useEffect(() => {
    if (!filteredUsers.length) {
      setSelectedUserId(null)
      setActiveAction('profile')
      return
    }

    const stillVisible = filteredUsers.some(user => user.id === selectedUserId)
    if (!stillVisible) {
      setSelectedUserId(filteredUsers[0].id)
      setActiveAction('profile')
    }
  }, [filteredUsers, selectedUserId])

  useEffect(() => {
    setStatusMessage('')
  }, [activeAction, selectedUserId])

  useEffect(() => {
    setBalanceForm(prev => ({ ...prev, amount: '' }))
    setPasswordForm({ password: '', confirm: '' })
  }, [selectedUserId])



  const activeUser = useMemo(
    () => filteredUsers.find(user => user.id === selectedUserId) ?? null,
    [filteredUsers, selectedUserId],
  )

  useEffect(() => {
    if (!activeUser) {
      setIsActionDialogOpen(false)
    }
  }, [activeUser])

  const handleActionTrigger = (actionId, user) => {
    if (!user) return
    setSelectedUserId(user.id)

    if (actionId === 'lock') {
      setLockTarget(user)
      return
    }

    if (actionId === 'logs') {
      alert("Transaction log is under development.")
      return
    }

    if (actionId === 'edit') {
     // navigate(`/users/${user.id}/edit`, { state: { user } })
      alert("Edit profile is under development.")
      return
    }

    if (!actionablePanels.includes(actionId)) return

    setActiveAction(actionId)
    if (isMobileView) {
      setIsActionDialogOpen(true)
    }
  }

  const handleLockConfirm = async() => {
    console.log("Locking user:", lockTarget);
    if (!lockTarget) return

    // setUsers(prev =>
    //   prev.map(user =>
    //     user.id === lockTarget.id ? { ...user, status: 'Locked' } : user,
    //   ),
    // )

    const payload = {
      userId: lockTarget.userId,
      isLock: true
    }

    try {
      setIsLoading(true);
      const response = await userService.lockUnlockUser(payload);

      if (response?.status === 200) {
        setStatusMessage(`${lockTarget.username} has been locked.`)
        setLockTarget(null)
        await fetchUserLists();
      } else {
        console.warn("Unexpected response:", response);
        setErrorMessage(`Failed to lock ${lockTarget.username}.`)
        setLockTarget(null)
      }
    } catch (e) {
      setErrorMessage(`Failed to lock ${lockTarget.username}.`)
      setLockTarget(null)
    } finally {
      setIsLoading(false);
    }
  }

  const handleSelectUser = userId => {
    if (!userId) {
      setSelectedUserId(null)
      setActiveAction('profile')
      setIsActionDialogOpen(false)
      return
    }

    setSelectedUserId(userId)
    setActiveAction('profile')
    if (isMobileView) {
      setIsActionDialogOpen(true)
    }
  }

  const handleBalanceChange = (key, value) => {
    setBalanceForm(prev => ({
      ...prev,
      [key]: value,
    }))
  }

  const handleBalanceSubmit = async() => {
    if (!activeUser) return
    const amount = Number(balanceForm.amount)
    if (!amount || Number.isNaN(amount)) return

    // const multiplier =
    //   balanceForm.type === 'debit' ? -1 : 1

    // setUsers(prev =>
    //   prev.map(user =>
    //     user.id === activeUser.id
    //       ? { ...user, balance: Math.max(0, (user.balance ?? 0) + multiplier * amount) }
    //       : user,
    //   ),
    // )

    const payload = {
      userId: activeUser.userId,
      amount: amount
    }

     try {
      setIsLoading(true);
      const response = await userService.updateUserBalance(payload,balanceForm.type);

       if (response?.status === 200) {
         setBalanceForm(prev => ({ ...prev, amount: '' }))
         setStatusMessage(`Balance updated for ${activeUser.username}.`)
         await fetchUserLists();
       } else {
         console.warn("Unexpected response:", response);
         setErrorMessage(`Failed to update balance for ${activeUser.username}.`)
         setUsers([]);
       }
    } catch (e) {
      setErrorMessage(`Failed to update balance for ${activeUser.username}.`)
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }

  const handlePasswordChange = (key, value) => {
    setPasswordForm(prev => ({
      ...prev,
      [key]: value,
    }))
  }

  const handlePasswordReset = async() => {
    if (!activeUser) return

    const payload = {
      NewPassword: passwordForm.confirm,
      userId: activeUser.userId
    }

    try {
      setIsLoading(true);
      const response = await userService.resetPassword(payload);

      if (response?.status === 200) {
        setPasswordForm({ password: '', confirm: '' })
        setStatusMessage(`Temporary password created for ${activeUser.username}.`)
      } else {
        console.warn("Unexpected response:", response);
        setErrorMessage(`Failed to reset password for ${activeUser.username}.`)
        setUsers([]);
      }
    }
    catch (e) {
      setErrorMessage(`Failed to reset password for ${activeUser.username}.`)
    } finally {
      setIsLoading(false);
    }
  }

  const handleUserTransactionFetch = async (payload) => {
    try {
      setIsLoading(true);
      const payload = {
        userId: activeUser.userId,
        startDate: "2023-01-01",
        endDate: "2024-12-31"
      }
      const response = await userService.getUserTransaction(payload);

      if (response?.status === 200) {
        // Handle successful response
      } else {
        console.warn("Unexpected response:", response);
        setErrorMessage(`Failed to fetch transaction for ${activeUser.username}.`)
        setUsers([]);
      }
    } catch (e) {
      console.error("Failed to fetch user transaction data", e);
      setErrorMessage(`Failed to fetch transaction for ${activeUser.username}.`)
    } finally {
      setIsLoading(false);
    }
  }

  const actionPanelProps = {
    action: activeAction,
    user: activeUser,
    transactionLogs: activeUser ? transactionLogs[1] ?? [] : [],
    balanceForm,
    onBalanceChange: handleBalanceChange,
    onBalanceSubmit: handleBalanceSubmit,
    balanceTypes: balanceTransactionTypes,
    passwordForm,
    onPasswordChange: handlePasswordChange,
    onPasswordReset: handlePasswordReset,
    statusMessage,
    errorMessage,
    onDismissStatus: () => {
      setStatusMessage('')
      setErrorMessage('')
    },
  }

  return (
    <Fragment>
      {
        isLoading ? <LoadingSpinner /> : null
      }
    <div className="w-full bg-customLightMilkWhite p-3 dark:bg-custom_dark_background">
      <div className="mx-auto flex w-full flex-col gap-2">
        {/* <header className="rounded-3xl border border-white/60 bg-gradient-to-br from-white via-customSlate to-customSky px-4 py-1 shadow-xl dark:border-white/10 dark:from-customSurface dark:via-customSurfaceMuted dark:to-customSurface"> */}
          <header className="rounded-3xl px-4">
            {/* <p className="text-sm font-semibold text-customAccent uppercase tracking-[0.2em]">
            Command Center
          </p> */}
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <h1 className="text-2xl font-bold text-customInk dark:text-customHeading">
                User Control
              </h1>
              <p className="text-sm text-customSecondaryText dark:text-customMuted">
                Monitor balances, review audit logs, and reset credentials in one panel.
              </p>
            </div>
          </header>

        <UserFilters
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          roleFilter={roleFilter}
          setRoleFilter={setRoleFilter}
          roleOptions={userRoles}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          statusOptions={userStatuses}
          isCompact={onlyWidth < 768}
        />

        <div className="grid gap-4 lg:grid-cols-5 grid-cols-1">
          <div className="lg:col-span-3 flex flex-col">
            <UserListTable
              users={paginatedUsers}
              selectedUserId={selectedUserId}
              onSelectUser={handleSelectUser}
              onAction={handleActionTrigger}
              actionDescriptors={actionConfig}
              totalCount={totalUsers}
              startIndex={paginationStart}
              isMobileView={isMobileView}
            />
            <PaginationV2
              startIndex={paginationStart}
              endIndex={paginationEnd}
              totalItems={totalUsers}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemLabel="users"
              isMobileView={isMobileView}
            />
          </div>

          {isMobileView ? null : (
            <div className="lg:col-span-2">
              <ActionPanels {...actionPanelProps} />
            </div>
          )}
        </div>
      </div>

      <ConfirmationDialog
        isOpen={!!lockTarget}
        setIsOpen={() => setLockTarget(null)}
        title="Lock this account?"
        contextText={
          lockTarget
            ? `Locking ${lockTarget.username} will prevent future login attempts until unlocked from admin tools.`
            : ''
        }
        confirmLabel="Lock user"
        cancelLabel="Cancel"
        handleSubmit={handleLockConfirm}
        appearance="warning"
      />
    </div>
    {isMobileView && activeUser ? (
      <CustomDialog
        open={isActionDialogOpen}
        isMobile
        width={400}
        padding={0}
        extraStyle={{ borderRadius: 10, maxHeight: '90vh', overflowY: 'auto' }}
      >
        <ActionPanels
          {...actionPanelProps}
          onClose={() => setIsActionDialogOpen(false)}
          isMobileView
        />
      </CustomDialog>
    ) : null}
    </Fragment>
  )
}

export default UserControlPage
